import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronRight, ArrowLeft, Tag, HelpCircle, Clock, Pencil } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { useTemplateDetail, type TemplateSection } from '@/api/hooks/useAttestations';
import { usePatchTemplate } from '@/api/hooks/useTemplateEdit';
import { useTemplateLastModified } from '@/api/hooks/useTemplateUsage';
import { useTemplateUsage } from '@/api/hooks/useTemplateUsage';
import { TemplateLifecycleActions } from '@/components/templates/TemplateLifecycleActions';
import { ImportedLicenseBanner, ImportedLicensePublishLine } from '@/features/templates/ImportedLicenseNotice';
import { TemplateUsagePanel } from '@/components/templates/TemplateUsagePanel';
import { TemplateVersionList } from '@/components/templates/TemplateVersionList';
import { useAuthStore } from '@/store/auth.store';

// ── Section row with expandable questions ────────────────────────────────────

function SectionRow({ section }: { section: TemplateSection }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="border border-border-subtle rounded-md overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-4 py-3 bg-bg-surface hover:bg-bg-hover transition-colors text-left"
      >
        <div className="flex items-center gap-3 min-w-0">
          {open ? (
            <ChevronDown className="h-4 w-4 text-text-secondary shrink-0" />
          ) : (
            <ChevronRight className="h-4 w-4 text-text-secondary shrink-0" />
          )}
          <span className="text-sm font-medium text-text-primary truncate">{section.name}</span>
        </div>
        <div className="flex items-center gap-3 shrink-0 ml-3">
          {section.weight_pct > 0 && (
            <span className="text-xs text-text-secondary font-mono">{section.weight_pct}% weight</span>
          )}
          <Badge variant="muted" className="text-[10px] py-0">
            {section.questions.length} question{section.questions.length !== 1 ? 's' : ''}
          </Badge>
        </div>
      </button>

      {open && (
        <div className="divide-y divide-border-subtle">
          {section.description && (
            <p className="px-4 py-2 text-xs text-text-secondary bg-bg-canvas">{section.description}</p>
          )}
          {section.questions.map((q, idx) => (
            <div key={q.id} className="px-4 py-3 bg-bg-canvas flex items-start gap-3">
              <span className="text-xs text-text-muted font-mono w-6 shrink-0 mt-0.5">{idx + 1}.</span>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-text-primary">{q.question_text}</p>
                {q.guidance_text && (
                  <p className="text-xs text-text-secondary mt-1">{q.guidance_text}</p>
                )}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Badge variant="muted" className="text-[10px] py-0">{q.response_type.replace(/_/g, ' ')}</Badge>
                {q.is_required && (
                  <Badge variant="amber" className="text-[10px] py-0">Required</Badge>
                )}
                {q.max_score > 0 && (
                  <span className="text-[10px] text-text-muted font-mono">{q.max_score}pts</span>
                )}
              </div>
            </div>
          ))}
          {section.questions.length === 0 && (
            <div className="px-4 py-3 bg-bg-canvas">
              <p className="text-xs text-text-muted italic">No questions in this section.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Lifecycle state badge ─────────────────────────────────────────────────────

function LifecycleStateBadge({ state }: { state: string }) {
  if (state === 'published') return null;
  const variants: Record<string, { variant: 'amber' | 'muted'; label: string }> = {
    disabled:  { variant: 'amber', label: 'Disabled' },
    archived:  { variant: 'muted', label: 'Archived' },
    draft:     { variant: 'muted', label: 'Draft' },
  };
  const cfg = variants[state];
  if (!cfg) return null;
  return <Badge variant={cfg.variant}>{cfg.label}</Badge>;
}

// Extract a human message from an API error (updateTemplate 400 → { errors: { name: [msg] } };
// other failures → a field/string message). Null → the caller shows a generic fallback.
function serverMessage(e: unknown): string | null {
  const errs = (e as { response?: { data?: { errors?: unknown } } })?.response?.data?.errors;
  if (typeof errs === 'string') return errs;
  if (errs && typeof errs === 'object') {
    const asMsg = (errs as { message?: unknown }).message;
    if (typeof asMsg === 'string') return asMsg;
    const first = Object.values(errs as Record<string, unknown>)[0];
    if (Array.isArray(first) && first.length) return String(first[0]);
    if (typeof first === 'string') return first;
  }
  return null;
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function TemplateDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { tenantId } = useAuthStore();
  const { data: template, isLoading, isError } = useTemplateDetail(id);
  const { data: lastModified } = useTemplateLastModified(id);
  const { data: usage } = useTemplateUsage(id);

  // Tenant copy-rename (UI-only): title/name only, always-available for tenant-owned
  // templates, duplicates allowed. Client validation mirrors the backend Zod
  // (min 1 / max 255, trimmed); the server stays the boundary — its rejection shows here.
  const patchTemplate = usePatchTemplate(id ?? '');
  const [renameOpen, setRenameOpen] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  const [nameError, setNameError] = useState<string | null>(null);

  function openRename(currentName: string) {
    setNameDraft(currentName);
    setNameError(null);
    patchTemplate.reset();
    setRenameOpen(true);
  }
  function submitRename() {
    const trimmed = nameDraft.trim();
    if (trimmed.length < 1) { setNameError('Name is required.'); return; }
    if (trimmed.length > 255) { setNameError('Name must be 255 characters or fewer.'); return; }
    setNameError(null);
    patchTemplate.mutate(
      { name: trimmed },
      {
        onSuccess: () => setRenameOpen(false),
        onError: (e) => setNameError(serverMessage(e) ?? 'Rename failed. Please try again.'),
      },
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-4 w-96" />
        <div className="space-y-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      </div>
    );
  }

  if (isError || !template) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
          <ArrowLeft className="mr-1.5 h-3.5 w-3.5" />
          Back
        </Button>
        <p className="text-sm text-text-secondary">Template not found or could not be loaded.</p>
      </div>
    );
  }

  const totalMaxScore = template.sections.reduce(
    (sum, s) => sum + s.questions.reduce((qs, q) => qs + q.max_score, 0),
    0,
  );

  const lifecycleState = template.lifecycle_state ?? 'published';

  return (
    <div className="space-y-6">
      {/* Back */}
      <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="-ml-2">
        <ArrowLeft className="mr-1.5 h-3.5 w-3.5" />
        Back
      </Button>

      {/* Header */}
      <div className="space-y-2">
        <div className="flex items-start gap-3 flex-wrap">
          <h1 className="text-2xl font-semibold text-text-primary flex-1">{template.name}</h1>
          <div className="flex gap-1.5 flex-wrap">
            {template.framework && (
              <Badge variant="muted">{template.framework}</Badge>
            )}
            {template.is_hub_template && (
              <Badge variant="sky">Hub</Badge>
            )}
            <LifecycleStateBadge state={lifecycleState} />
            <Badge variant="muted" className="font-mono text-xs">
              v{template.version} · rev {template.content_revision}
            </Badge>
          </div>
        </div>
        {template.description && (
          <p className="text-sm text-text-secondary max-w-2xl">{template.description}</p>
        )}
      </div>

      {/* ITEM 3a(i) — imported-template licensing banner (hub_template_id != null). */}
      {template.hub_template_id && <ImportedLicenseBanner />}

      {/* D-VA-3 lifecycle actions + edit — tenant-owned templates only */}
      {!template.is_hub_template && tenantId && (
        <div className="flex flex-wrap gap-2 items-center">
          {/* Tenant copy-rename — always-available (outside the lifecycle gate), tenant-owned
              (inherits this guard); title/name only. A hub row shows no control (guard) and
              is un-renameable at the endpoint (tenant_id IS NOT NULL). */}
          <Button size="sm" variant="outline" onClick={() => openRename(template.name)}>
            <Pencil className="mr-1.5 h-3.5 w-3.5" />
            Rename
          </Button>
          {(lifecycleState === 'draft' || lifecycleState === 'published') && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => navigate(`/templates/${template.id}/edit`)}
            >
              <Pencil className="mr-1.5 h-3.5 w-3.5" />
              {lifecycleState === 'draft' ? 'Build Template' : 'Edit & Republish'}
            </Button>
          )}
          <TemplateLifecycleActions
            templateId={template.id}
            lifecycleState={lifecycleState}
            isHubTemplate={template.is_hub_template}
            tenantId={tenantId}
            activeAssessmentCount={usage?.active_count ?? 0}
          />
        </div>
      )}

      {/* ITEM 3a(iii) — passive licensing line near Publish (imported only). */}
      {template.hub_template_id && <ImportedLicensePublishLine />}

      {/* Meta cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card className="bg-bg-surface">
          <CardContent className="pt-4 pb-3">
            <p className="text-xs text-text-secondary mb-1">Questions</p>
            <p className="text-2xl font-semibold text-text-primary">{template.total_questions}</p>
          </CardContent>
        </Card>
        <Card className="bg-bg-surface">
          <CardContent className="pt-4 pb-3">
            <p className="text-xs text-text-secondary mb-1">Sections</p>
            <p className="text-2xl font-semibold text-text-primary">{template.sections.length}</p>
          </CardContent>
        </Card>
        <Card className="bg-bg-surface">
          <CardContent className="pt-4 pb-3">
            <p className="text-xs text-text-secondary mb-1">Max score</p>
            <p className="text-2xl font-semibold text-text-primary">{totalMaxScore}</p>
          </CardContent>
        </Card>
        <Card className="bg-bg-surface">
          <CardContent className="pt-4 pb-3">
            <p className="text-xs text-text-secondary mb-1">Pass threshold</p>
            <p className="text-2xl font-semibold text-text-primary">{template.pass_threshold_pct}%</p>
          </CardContent>
        </Card>
      </div>

      {/* Source + dates */}
      <div className="flex flex-wrap gap-4 text-xs text-text-secondary">
        <span className="flex items-center gap-1">
          <Tag className="h-3.5 w-3.5" />
          {template.source_label}
        </span>
        <span className="flex items-center gap-1">
          <HelpCircle className="h-3.5 w-3.5" />
          Created {new Date(template.created_at).toLocaleDateString()}
        </span>
        {lastModified && (
          <span className="flex items-center gap-1">
            <Clock className="h-3.5 w-3.5" />
            Last modified {new Date(lastModified.at).toLocaleDateString()}
            {lastModified.actor_email && ` by ${lastModified.actor_email}`}
          </span>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Sections (takes 2/3 width on large screens) */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Sections &amp; Questions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {template.sections.length === 0 ? (
                <p className="text-sm text-text-secondary py-4 text-center">No sections defined for this template.</p>
              ) : (
                template.sections
                  .sort((a, b) => a.display_order - b.display_order)
                  .map((section) => (
                    <SectionRow key={section.id} section={section} />
                  ))
              )}
            </CardContent>
          </Card>
        </div>

        {/* Usage panel + version history (1/3 width on large screens) */}
        <div className="space-y-4">
          <TemplateUsagePanel templateId={template.id} />
          {!template.is_hub_template && lifecycleState === 'published' && (
            <TemplateVersionList templateId={template.id} />
          )}
        </div>
      </div>

      {/* Rename modal — title/name only; validates min1/max255 trimmed (mirrors backend Zod),
          duplicates allowed; server rejection surfaces in-modal. */}
      <Dialog open={renameOpen} onOpenChange={(o) => { if (!patchTemplate.isPending) setRenameOpen(o); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rename template</DialogTitle>
            <DialogDescription>
              Give this template your own name. This changes only its title, not its content.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Input
              aria-label="Template name"
              value={nameDraft}
              maxLength={255}
              autoFocus
              onChange={(e) => setNameDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); submitRename(); } }}
            />
            {nameError && <p className="text-xs text-accent-rose">{nameError}</p>}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRenameOpen(false)} disabled={patchTemplate.isPending}>
              Cancel
            </Button>
            <Button onClick={submitRename} disabled={patchTemplate.isPending || nameDraft.trim().length < 1}>
              {patchTemplate.isPending ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
